'use client'

import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import {
  useDeleteScheduledTaskMutation,
  useGetScheduledTasksQuery,
  useSkipNextScheduledTaskMutation,
  useUpdateScheduledTaskMutation,
} from '@/lib/features/scheduled-tasks/scheduledTasksApi'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ScheduledTaskCard } from './ScheduledTaskCard'
import { ScheduledTaskModal } from './ScheduledTaskModal'

const PAGE_SIZE = 20

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

function formatRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return 'Never'
  const diffSecs = (new Date(date).getTime() - Date.now()) / 1000
  if (Math.abs(diffSecs) < 60) return rtf.format(Math.round(diffSecs), 'seconds')
  if (Math.abs(diffSecs) < 3600) return rtf.format(Math.round(diffSecs / 60), 'minutes')
  if (Math.abs(diffSecs) < 86400) return rtf.format(Math.round(diffSecs / 3600), 'hours')
  return rtf.format(Math.round(diffSecs / 86400), 'days')
}

export default function ScheduledTasksClient() {
  const [offset, setOffset] = useState(0)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<
    'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'PAUSED' | undefined
  >(undefined)
  const searchParams = useSearchParams()

  // Deep-link support: ?task=<id>&execution=<id>
  const taskParam = searchParams.get('task')
  const executionParam = searchParams.get('execution')

  useEffect(() => {
    if (taskParam) {
      setSelectedTaskId(taskParam)
    }
  }, [taskParam])

  const {
    data: tasksData,
    isError,
    isLoading,
    isFetching,
  } = useGetScheduledTasksQuery({
    status: statusFilter,
    limit: PAGE_SIZE,
    offset,
  })

  const [deleteTask] = useDeleteScheduledTaskMutation()
  const [updateTask] = useUpdateScheduledTaskMutation()
  const [skipNextTask] = useSkipNextScheduledTaskMutation()

  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      try {
        await deleteTask(taskId).unwrap()
        toast.success('Task deleted successfully')
      } catch (error) {
        console.error('Failed to delete scheduled task:', error)
        toast.error('Failed to delete task. Please try again.')
      }
    },
    [deleteTask]
  )

  const handleFreezeTask = useCallback(
    async (taskId: string) => {
      try {
        await updateTask({ id: taskId, input: { status: 'PAUSED' } }).unwrap()
        toast.success('Task frozen')
      } catch (error) {
        console.error('Failed to freeze task:', error)
        toast.error('Failed to freeze task. Please try again.')
      }
    },
    [updateTask]
  )

  const handleUnfreezeTask = useCallback(
    async (taskId: string) => {
      try {
        await updateTask({ id: taskId, input: { status: 'PENDING' } }).unwrap()
        toast.success('Task unfrozen')
      } catch (error) {
        console.error('Failed to unfreeze task:', error)
        toast.error('Failed to unfreeze task. Please try again.')
      }
    },
    [updateTask]
  )

  const handleSkipNextTask = useCallback(
    async (taskId: string) => {
      try {
        await skipNextTask(taskId).unwrap()
        toast.success('Skipped next run')
      } catch (error) {
        console.error('Failed to skip task:', error)
        toast.error('Failed to skip task. Please try again.')
      }
    },
    [skipNextTask]
  )

  const handleToggleFavourite = useCallback(
    async (taskId: string, current: boolean) => {
      try {
        await updateTask({ id: taskId, input: { bookmark: !current } }).unwrap()
      } catch (error) {
        console.error('Failed to toggle favourite:', error)
        toast.error('Failed to update favourite. Please try again.')
      }
    },
    [updateTask]
  )

  const handleToggleEmailNotification = useCallback(
    async (taskId: string, currentEnabled: boolean) => {
      try {
        await updateTask({
          id: taskId,
          input: { notificationSettings: { email: { enabled: !currentEnabled } } },
        }).unwrap()
      } catch (error) {
        console.error('Failed to toggle email notification:', error)
        toast.error('Failed to update notification settings. Please try again.')
      }
    },
    [updateTask]
  )

  const handleLoadMore = useCallback(() => {
    setOffset((prev) => prev + PAGE_SIZE)
  }, [])

  const tasks = tasksData?.tasks || []
  const pagination = tasksData?.pagination

  if (isLoading) {
    return (
      <div className="font-space-grotesk flex h-full w-full flex-col p-4">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Scheduled Tasks</h1>
          <p className="mt-1 text-sm text-gray-600">Automate tasks to run on a schedule</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 rounded-3xl" />
          ))}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="font-space-grotesk flex h-full w-full flex-col items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Failed to load scheduled tasks</h2>
          <p className="mt-2 text-sm text-gray-500">Please try again later.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="font-space-grotesk flex h-full w-full flex-col p-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Scheduled Tasks</h1>
          <p className="mt-1 text-sm text-gray-600">Automate tasks to run on a schedule</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter || ''}
            onChange={(e) => {
              const value = e.target.value
              setStatusFilter(value === '' ? undefined : (value as typeof statusFilter))
              setOffset(0)
            }}
            className="rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none"
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Scheduled</option>
            <option value="RUNNING">Running</option>
            <option value="PAUSED">Frozen</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
            <option value="CANCELLED">Skipped</option>
          </select>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900">No scheduled tasks yet</h2>
            <p className="mt-2 text-sm text-gray-600">
              Click the Schedule button on any completed task in chat to create one
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {tasks.map((task) => (
              <ScheduledTaskCard
                key={task.id}
                task={task}
                onClick={() => setSelectedTaskId(task.id)}
                onDelete={() => handleDeleteTask(task.id)}
                onFreeze={() => handleFreezeTask(task.id)}
                onUnfreeze={() => handleUnfreezeTask(task.id)}
                onSkipNext={() => handleSkipNextTask(task.id)}
                onToggleFavourite={() => handleToggleFavourite(task.id, task.bookmark)}
                onToggleEmailNotification={() =>
                  handleToggleEmailNotification(
                    task.id,
                    task.notificationSettings?.email?.enabled ?? false
                  )
                }
                formatRelativeTime={formatRelativeTime}
              />
            ))}
          </div>

          {pagination?.hasMore && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={handleLoadMore}
                disabled={isFetching}
                className="rounded-2xl border border-gray-200 bg-white px-6 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
              >
                {isFetching ? (
                  <span className="flex items-center gap-2">
                    <Spinner size="sm" />
                    Loading...
                  </span>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
        </>
      )}

      {selectedTaskId && (
        <ScheduledTaskModal
          taskId={selectedTaskId}
          isOpen={!!selectedTaskId}
          onClose={() => setSelectedTaskId(null)}
          initialExecutionId={executionParam ?? undefined}
          onUpdate={async (id, input) => {
            try {
              await updateTask({ id, input }).unwrap()
              toast.success('Task updated successfully')
            } catch (error) {
              console.error('Failed to update task:', error)
              toast.error('Failed to update task. Please try again.')
            }
          }}
          onDelete={handleDeleteTask}
        />
      )}
    </div>
  )
}
